/*
 * Nuvoro search inputs.
 * <search-form>        plain search field: keeps the clear button in sync.
 * <predictive-search>  search field with live results fetched through the
 *                      Section Rendering API (section "predictive-search").
 * window.SearchForm is exposed so other components can extend it.
 */
(() => {
  const waitThenRun = (callback, wait) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), wait);
    };
  };

  const suggestUrl = () => {
    if (window.routes && window.routes.predictive_search_url) return window.routes.predictive_search_url;
    const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${base}search/suggest`;
  };

  if (!window.SearchForm) {
    window.SearchForm = class SearchForm extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input[type="search"]');
        this.resetButton = this.querySelector('button[type="reset"]');

        if (this.input && this.input.form) {
          this.input.form.addEventListener('reset', this.onFormReset.bind(this));
          this.input.addEventListener(
            'input',
            waitThenRun((event) => this.onChange(event), 300)
          );
        }
      }

      toggleResetButton() {
        if (!this.resetButton || !this.input) return;
        this.resetButton.classList.toggle('hidden', this.input.value.length === 0);
      }

      onChange() {
        this.toggleResetButton();
      }

      shouldResetForm() {
        return !document.querySelector('[aria-selected="true"] a');
      }

      onFormReset(event) {
        // Let a selected suggestion win over the reset button.
        event.preventDefault();
        if (!this.shouldResetForm()) return;
        this.input.value = '';
        this.input.focus();
        this.toggleResetButton();
      }
    };
  }

  if (!customElements.get('search-form')) {
    customElements.define('search-form', class extends window.SearchForm {});
  }

  if (customElements.get('predictive-search')) return;

  const sharedCache = new Map();

  class PredictiveSearch extends window.SearchForm {
    constructor() {
      super();
      this.panel = this.querySelector('[data-predictive-search]');
      this.statusNode = this.querySelector('.predictive-search-status');
      this.isMain = this.dataset.main === 'true';
      this.isOpen = false;
      this.searchTerm = '';
      this.request = null;

      if (!this.input || !this.panel) return;

      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    disconnectedCallback() {
      this.abortRequest();
    }

    currentQuery() {
      return this.input.value.trim();
    }

    onChange() {
      super.onChange();
      const term = this.currentQuery();

      if (!term.length) {
        this.searchTerm = '';
        this.abortRequest();
        this.close(true);
        return;
      }

      if (term !== this.searchTerm) {
        const label = this.querySelector('[data-predictive-search-search-for-text]');
        if (label && this.searchTerm) {
          // Swap only the visible term so the "search for" row stays in sync while loading.
          label.textContent = label.textContent.split(this.searchTerm).join(term);
        }
      }

      this.searchTerm = term;
      this.fetchResults(term);
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortRequest();
        this.close(true);
      }
    }

    onFormSubmit(event) {
      if (!this.currentQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    onFocus() {
      const term = this.currentQuery();
      if (!term.length) return;
      if (term !== this.searchTerm) {
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.fetchResults(term);
      }
    }

    onFocusOut() {
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (!this.currentQuery().length) this.close(true);
      event.preventDefault();

      switch (event.code) {
        case 'ArrowUp':
          this.moveSelection(-1);
          break;
        case 'ArrowDown':
          this.moveSelection(1);
          break;
        case 'Enter':
          this.activateSelection();
          break;
        case 'Escape':
          this.close();
          break;
        default:
          break;
      }
    }

    onKeydown(event) {
      // Keep the caret where it is while moving through the list.
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
    }

    selectableOptions() {
      return Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
        (node) => node.offsetParent !== null
      );
    }

    moveSelection(step) {
      if (!this.hasAttribute('open')) return;
      const options = this.selectableOptions();
      if (!options.length) return;

      const current = this.querySelector('[aria-selected="true"]');
      let index = current ? options.indexOf(current) + step : step > 0 ? 0 : -1;

      if (current) current.setAttribute('aria-selected', 'false');

      if (index < 0) {
        // Moving above the first option hands control back to the input.
        this.input.setAttribute('aria-activedescendant', '');
        this.input.focus();
        return;
      }
      if (index >= options.length) index = 0;

      const next = options[index];
      next.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', next.id || '');
      if (typeof next.scrollIntoView === 'function') next.scrollIntoView({ block: 'nearest' });
    }

    activateSelection() {
      const chosen = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      if (chosen) chosen.click();
    }

    abortRequest() {
      if (this.request) {
        this.request.abort();
        this.request = null;
      }
    }

    fetchResults(term) {
      const key = term.replace(/\s+/g, '-').toLowerCase();
      this.announceLoading();

      if (sharedCache.has(key)) {
        this.renderResults(sharedCache.get(key));
        return;
      }

      this.abortRequest();
      const controller = new AbortController();
      this.request = controller;

      const url = `${suggestUrl()}?q=${encodeURIComponent(term)}&section_id=predictive-search`;
      fetch(url, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Predictive search failed (${response.status})`);
          return response.text();
        })
        .then((text) => {
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const section = parsed.querySelector('#shopify-section-predictive-search');
          const markup = section ? section.innerHTML : '';
          sharedCache.set(key, markup);
          if (this.request === controller) this.request = null;
          if (this.currentQuery() === term) this.renderResults(markup);
        })
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          this.removeAttribute('loading');
          this.close();
        });
    }

    announce(text) {
      if (!this.statusNode) return;
      this.statusNode.setAttribute('aria-hidden', 'false');
      this.statusNode.textContent = text;
      window.setTimeout(() => this.statusNode.setAttribute('aria-hidden', 'true'), 1000);
    }

    announceLoading() {
      this.setAttribute('loading', 'true');
      this.announce(this.dataset.loadingText || '');
    }

    renderResults(markup) {
      this.panel.innerHTML = markup;
      this.setAttribute('results', 'true');
      this.removeAttribute('loading');
      const summary = this.querySelector('[data-predictive-search-live-region-count-value]');
      this.announce(summary ? summary.textContent.trim() : '');
      this.open();
    }

    limitPanelHeight() {
      if (this.isMain || !this.panel.classList.contains('predictive-search--header')) return;
      const top = this.panel.getBoundingClientRect().top;
      if (top > 0) this.panel.style.maxHeight = `${Math.max(window.innerHeight - top - 16, 200)}px`;
    }

    open() {
      this.setAttribute('open', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
      this.limitPanelHeight();
    }

    close(clearTerm = false) {
      this.closeResults(clearTerm);
      this.isOpen = false;
    }

    closeResults(clearTerm = false) {
      if (!this.input) return;
      if (clearTerm) {
        this.input.value = '';
        this.removeAttribute('results');
        this.toggleResetButton();
      }
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      if (this.panel) this.panel.style.maxHeight = '';
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
  window.PredictiveSearch = PredictiveSearch;
})();
